import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { EmitError } from "../../../utils/EmitError";
import { formatCpf, formatCurrency, formatPhone } from "../../../utils/Masks";
import GiveawaysService from "../../../services/http/services/GiveawaysService";
import { useGetColumnSearchProps } from "../../../hooks/FilterTable/stringClearFiltros";
import { Tag } from "antd";
import { poeValorComPontosEmMil } from "../../../utils/Conversores";
import { IGiveaway, IGiveawayParticipant } from "../../../interfaces";

export interface ITableData extends IGiveawayParticipant {
    key: number | undefined;
    app_user_cpf: string;
    app_user_name: string;
    app_user_phone: string;
}

export default function useGiveawayParticipants() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [giveaway, setGiveaway] = useState<IGiveaway>();
    const refFuncaoLimparFiltrosString = useRef<any>(null);

    const { id } = useParams();
    const navigate = useNavigate();

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'app_user_name',
            key: 'app_user_name',
            sorter: (a: ITableData, b: ITableData) => a.app_user_name.localeCompare(b.app_user_name),
            showSorterTooltip: false,
            ...useGetColumnSearchProps({dataIndex: 'app_user_name', ref: refFuncaoLimparFiltrosString}),
        },
        {
            title: 'CPF',
            dataIndex: 'app_user_cpf',
            key: 'app_user_cpf',
            ...useGetColumnSearchProps({dataIndex: 'app_user_cpf', ref: refFuncaoLimparFiltrosString, formatter: formatCpf}),
        },
        {
            title: 'Telefone',
            dataIndex: 'app_user_phone',
            key: 'app_user_phone',
            ...useGetColumnSearchProps({dataIndex: 'app_user_phone', ref: refFuncaoLimparFiltrosString, formatter: formatPhone}),
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            sorter: (a: IGiveawayParticipant, b: IGiveawayParticipant) => {
                const aTickets = a.total_tickets ?? 0;
                const bTickets = b.total_tickets ?? 0;
                return aTickets - bTickets;
            },
            showSorterTooltip: false,
            render: (value?: number) => <Tag>{poeValorComPontosEmMil(value) ?? '0'}</Tag>,
        },
        {
            title: 'Saldo',
            dataIndex: 'balance_tickets',
            key: 'balance_tickets',
            render: (record: number) => `R$ ${formatCurrency(record)}`,
            sorter: (a: ITableData, b: ITableData) => a.balance_tickets! - b.balance_tickets!,
            showSorterTooltip: false,
        },
    ]

    const setTableDataFromRequest = useCallback((data: IGiveawayParticipant[]) => {
        if (!data) {
            return;
        }

        const newData = data.map((participant) => ({
            ...participant,
            key: participant.id,
            app_user_name: participant?.name!,
            app_user_cpf: participant?.cpf!,
            app_user_phone: participant?.phone!,
        }));
        setTableData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadParticipants() {
            try {
                setIsLoading(true);

                const [giveawayData, giveawayParticipantsData] = await Promise.all([
                    GiveawaysService.getGiveawayById({id: Number(id), signal: controller.signal}),
                    GiveawaysService.listGiveawayParticipantsById({id: Number(id), signal: controller.signal})
                ]);

                setGiveaway(giveawayData);
                setTableDataFromRequest(giveawayParticipantsData);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'giveawayWinners_aosdpasdpasd');
                setIsLoading(false);
            }
        }

        loadParticipants();

        return () => {
            controller.abort();
        }
    }, [setTableDataFromRequest, navigate, id]);

    const handleClickGoBack = useCallback(() => {
        window.history.back();
    }, []);

    return {
        columns,
        isLoading,
        tableData,
        handleClickGoBack,
        giveaway
    }
}
